import api from "./api";
import { AboutPageData, BusinessInfo } from "../types/storesetting";
import { validateImage, resizeImage } from '../lib/image';

export type SectionStatus = "active" | "inactive";
export type ValidationErrors = Record<string, string[]>;

export interface SocialLink {
  name: string;
  link: string;
}

export interface FooterSetting {
  id: number;
  company_id: number;
  contact_email: string;
  contact_phone: string;
  social_links: SocialLink[];
  footer_text: string;
  address: string;
  created_at: string;
  updated_at: string;
}

export interface WebsiteThemeSettings {
  id: number;
  company_id: number;
  title: string;
  booking_url?: string | null;
  booking_button_label?: string | null;
  template?: string | null;
  primary_color: string;
  secondary_color: string;
  heading_color?: string | null;
  text_color?: string | null;
  custom_css: string;
  logo: string | null;
  favicon: string | null;
  is_logo_visible: number; // 0 or 1
  created_at: string | null;
  updated_at: string;
}

export interface ThemeColorDefaults {
  primary_color: string;
  secondary_color: string;
  heading_color: string;
  text_color: string;
}

export interface FrontFaq {
  language_name: string;
  question: string;
  answer: string;
  faq_id: number;
}

export interface WebsiteSectionDefinition {
  id: number;
  name: string;
  text: string | null;
  status: string;
}

export interface WebsiteSection {
  id: number;
  company_id: number;
  website_section_id: number;
  status: "active" | "inactive";
  created_at: string | null;
  updated_at: string;
  section: WebsiteSectionDefinition;
}

export interface AboutSectionData {
  id: number;
  company_id: number;
  template: string;
  heading: string;
  subheading: string;
  image: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface AboutUsPageData {
  id: number;
  company_id: number;
  heading: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface GalleryImage {
  id: number;
  company_id: number;
  template: string;
  image: string;
  created_at: string;
  updated_at: string;
}

export interface HeroSlider {
  id: number;
  company_id: number;
  image: string;
  image_url: string;
  identifier: string;
  template: string;
  have_content: string;
  subheading: string;
  heading: string;
  content: string;
  action_button: string | null;
  custom_label: string | null;
  url: string | null;
  open_tab: string;
  content_alignment: string | null;
  created_at: string;
  updated_at: string;
}

export interface WebsiteSettingsResponse {
  company_subdomain: string;
  images: any[];
  footer_setting: FooterSetting;
  website_theme_settings: WebsiteThemeSettings;
  theme_template?: string;
  theme_template_label?: string;
  theme_defaults?: ThemeColorDefaults;
  front_faqs: FrontFaq[];
  front_widgets: any[];
  sections: WebsiteSection[];
  about_section: AboutSectionData;
  about_us_page: AboutUsPageData;
  // Optional extra standalone pages if provided by backend
  cancel_policy_page?: AboutUsPageData;
  privacy_policy_page?: AboutUsPageData;
  terms_conditions_page?: AboutUsPageData;
  galleries: GalleryImage[];
  testimonials: any[];
}

// Generic single website page (About, policies, etc.)
export interface WebsitePage {
  id: number;
  company_id: number;
  title: string;
  description: string;
  key: string;
  created_at: string;
  updated_at: string;
}

// -------------------- Update Section Status --------------------
export const updateSectionStatus = async (
  sectionId: number,
  status: SectionStatus
) => {
  try {
    const response = await api.patch(`/admin/website-settings/sections/${sectionId}/status`, {
      status,
    });
    console.log("Update Section Status response:", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = msgs.join(" ");
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    console.error("Error updating section status:", err);
    throw err;
  }
};

// ----------------------------- Generic Website Pages ------------------------------------

export interface UpsertWebsitePagePayload {
  id?: number;
  company_id: number;
  title: string;
  description: string;
  key: string;
}

export const getWebsitePage = async (key: string): Promise<WebsitePage | null> => {
  try {
    const response = await api.get(`/admin/website-settings/page/${key}`, {
      headers: { Accept: "application/json" },
    });
    return response.data as WebsitePage;
  } catch (err: any) {
    if (err?.response?.status === 404) {
      return null;
    }
    throw err;
  }
};

export const createOrUpdateWebsitePage = async (
  payload: UpsertWebsitePagePayload
): Promise<WebsitePage> => {
  try {
    const response = await api.post(
      "/admin/website-settings/page/create-or-update",
      payload,
      {
        headers: { Accept: "application/json" },
      }
    );
    return response.data?.data || response.data;
  } catch (err: any) {
    if (err?.response?.status === 400 && err.response.data) {
      // Laravel validator errors
      const rawErrors = err.response.data;
      const fieldErrors: Record<string, string> = {};
      Object.entries(rawErrors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    throw err;
  }
};

// -----------------------------Update About Section------------------------------------
export const updateAboutSection = async (
  businessInfo: BusinessInfo,
) => {
  try {

    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("heading", businessInfo.heading);
    formData.append("subheading", businessInfo.subheading);
    formData.append("description", businessInfo.description);

    if (businessInfo.image instanceof File) {

      // if big, compress/rescale (optional)
      let uploadBlob: Blob = businessInfo.image;
      if (businessInfo.image.size > 1.5 * 1024 * 1024) {
        uploadBlob = await resizeImage(businessInfo.image, 1600, 0.8);
      }
      formData.append("image", new File([uploadBlob], businessInfo.image.name, { type: uploadBlob.type }));
    }

    try {
      const response = await api.post("/admin/website-settings/about-section", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Accept": "application/json"
        },
      });
      console.log("About Section updated successfully:", response.data);
      return response.data;
    } catch (reqErr: any) {
      if (reqErr.response?.status === 404) {
        console.warn("About section update failed (404). Trying to create new settings...");
        formData.delete("_method");
        const retryResponse = await api.post("/admin/website-settings/about-section", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "Accept": "application/json"
          },
        });
        return retryResponse.data;
      }
      throw reqErr;
    }
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    throw err;
  }
};

export const updateAboutPage = async (data: AboutPageData) => {
  console.log("Sending About Page update:", data);
  try {
    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("heading", data.heading);
    formData.append("description", data.description);

    try {
      const response = await api.post("/admin/website-settings/about-page", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Accept": "application/json",
        },
      });

      console.log("About page updated successfully:", response.data);
      return response.data;
    } catch (reqErr: any) {
      if (reqErr.response?.status === 404) {
        console.warn("About page update failed (404). Trying to create...");
        formData.delete("_method");
        const retryResponse = await api.post("/admin/website-settings/about-page", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "Accept": "application/json",
          },
        });
        return retryResponse.data;
      }
      throw reqErr;
    }
  } catch (error: any) {
    if (error?.response?.status === 422 && error.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(error.response.data.errors).forEach(([key, messages]: any) => {
        fieldErrors[key] = messages.join(" ");
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }

    console.error("Error updating About Page:", error);
    throw error;
  }
};

export const updateCancelPolicyPage = async (data: AboutPageData) => {
  console.log("Sending Cancel Policy Page update:", data);
  try {
    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("heading", data.heading);
    formData.append("description", data.description);

    try {
      const response = await api.post(
        "/admin/website-settings/cancel-policy-page",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
        }
      );

      console.log("Cancel Policy page updated successfully:", response.data);
      return response.data;
    } catch (reqErr: any) {
      if (reqErr.response?.status === 404) {
        console.warn("Cancel Policy page update failed (404). Trying to create...");
        formData.delete("_method");
        const retryResponse = await api.post(
          "/admin/website-settings/cancel-policy-page",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Accept: "application/json",
            },
          }
        );
        return retryResponse.data;
      }
      throw reqErr;
    }
  } catch (error: any) {
    if (error?.response?.status === 422 && error.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(error.response.data.errors).forEach(
        ([key, messages]: any) => {
          fieldErrors[key] = messages.join(" ");
        }
      );
      throw { type: "serverValidation", errors: fieldErrors };
    }

    console.error("Error updating Cancel Policy Page:", error);
    throw error;
  }
};

export const updatePrivacyPolicyPage = async (data: AboutPageData) => {
  console.log("Sending Privacy Policy Page update:", data);
  try {
    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("heading", data.heading);
    formData.append("description", data.description);

    try {
      const response = await api.post(
        "/admin/website-settings/privacy-policy-page",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
        }
      );

      console.log("Privacy Policy page updated successfully:", response.data);
      return response.data;
    } catch (reqErr: any) {
      if (reqErr.response?.status === 404) {
        console.warn("Privacy Policy page update failed (404). Trying to create...");
        formData.delete("_method");
        const retryResponse = await api.post(
          "/admin/website-settings/privacy-policy-page",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Accept: "application/json",
            },
          }
        );
        return retryResponse.data;
      }
      throw reqErr;
    }
  } catch (error: any) {
    if (error?.response?.status === 422 && error.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(error.response.data.errors).forEach(
        ([key, messages]: any) => {
          fieldErrors[key] = messages.join(" ");
        }
      );
      throw { type: "serverValidation", errors: fieldErrors };
    }

    console.error("Error updating Privacy Policy Page:", error);
    throw error;
  }
};

export const updateTermsConditionsPage = async (data: AboutPageData) => {
  console.log("Sending Terms & Conditions Page update:", data);
  try {
    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("heading", data.heading);
    formData.append("description", data.description);

    try {
      const response = await api.post(
        "/admin/website-settings/terms-conditions-page",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
        }
      );

      console.log("Terms & Conditions page updated successfully:", response.data);
      return response.data;
    } catch (reqErr: any) {
      if (reqErr.response?.status === 404) {
        console.warn(
          "Terms & Conditions page update failed (404). Trying to create..."
        );
        formData.delete("_method");
        const retryResponse = await api.post(
          "/admin/website-settings/terms-conditions-page",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Accept: "application/json",
            },
          }
        );
        return retryResponse.data;
      }
      throw reqErr;
    }
  } catch (error: any) {
    if (error?.response?.status === 422 && error.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(error.response.data.errors).forEach(
        ([key, messages]: any) => {
          fieldErrors[key] = messages.join(" ");
        }
      );
      throw { type: "serverValidation", errors: fieldErrors };
    }

    console.error("Error updating Terms & Conditions Page:", error);
    throw error;
  }
};

/** Normalize file so Laravel mimes:jpeg,png,jpg,gif accepts it (correct MIME + extension). */
function normalizeImageFileForUpload(file: File): File {
  const validTypes = ["image/jpeg", "image/png", "image/gif"] as const;
  const typeToExt: Record<string, string> = {
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/gif": ".gif",
  };
  let mime: string = file.type;
  if (!validTypes.includes(mime as any)) {
    if (mime === "image/jpg") mime = "image/jpeg";
    else {
      const ext = (file.name.match(/\.[^.]+$/i) || [""])[0].toLowerCase();
      if (ext === ".png") mime = "image/png";
      else if (ext === ".gif") mime = "image/gif";
      else mime = "image/jpeg";
    }
  }
  const ext = typeToExt[mime] || ".jpg";
  const baseName = file.name.replace(/\.[^.]+$/i, "") || "image";
  const newName = `${baseName}${ext}`;
  return new File([file], newName, { type: mime });
}

export const uploadGalleryImages = async (files: File[]) => {
  try {
    if (!files || files.length === 0) {
      throw new Error("No files provided for upload.");
    }

    const formData = new FormData();
    // Backend expects 'file' as array (required|array, file.* => image|mimes:jpeg,png,jpg,gif)
    files.forEach((file, index) => {
      const normalized = normalizeImageFileForUpload(file);
      formData.append(`file[${index}]`, normalized, normalized.name);
    });

    // Do not set Content-Type: let axios set multipart/form-data with boundary so the server can parse the body
    const response = await api.post(
      "/admin/website-settings/gallery",
      formData,
      {
        headers: {
          "Accept": "application/json",
        },
      }
    );
    return response.data;
  } catch (err) {
    console.error("Failed to upload gallery images:", err);
    throw err;
  }
};


export const deleteGalleryImage = async (imageId: number) => {
  try {
    const response = await api.delete(`/admin/website-settings/gallery/${imageId}`);
    console.log(`Gallery image ${imageId} deleted successfully:`, response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = msgs.join(" ");
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    console.error(`Failed to delete gallery image ${imageId}:`, err.response?.data || err);
    throw err;
  }
};

export const getWebsiteSettings = async (): Promise<WebsiteSettingsResponse> => {
  try {
    const response = await api.get("/admin/website-settings", {
      headers: {
        Accept: "application/json",
      },
    });
    console.log("Website settings fetched:", response.data);
    return response.data?.data || response.data;
  } catch (err) {
    console.error("Error fetching website settings:", err);
    throw err;
  }
};

export const updateFooterSettings = async (data: any) => {
  try {
    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("contact_email", data.contactEmail);
    formData.append("footer_text", data.copyright);

    // Append social links as associative array or array
    if (data.social_links) {
      if (Array.isArray(data.social_links)) {
        data.social_links.forEach((link: string, index: number) => {
          formData.append(`social_links[${index}]`, link);
        });
      } else if (typeof data.social_links === 'object') {
        Object.entries(data.social_links).forEach(([key, link]) => {
          if (typeof link === 'string') {
            formData.append(`social_links[${key}]`, link);
          }
        });
      }
    }

    try {
      const response = await api.post("/admin/website-settings/footer/1", formData, {
        headers: {
          "Accept": "application/json"
        }
      });
      console.log("Footer settings updated:", response.data);
      return response.data;
    } catch (reqErr: any) {
      if (reqErr.response?.status === 404) {
        console.warn("Footer update failed (404). Trying to create...");
        formData.delete("_method");
        const retryResponse = await api.post("/admin/website-settings/footer/1", formData, {
          headers: {
            "Accept": "application/json"
          }
        });
        return retryResponse.data;
      }
      throw reqErr;
    }
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = msgs.join(" ");
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    console.error("Error updating footer settings:", err);
    throw err;
  }
};

// -----------------------------Get Theme Settings------------------------------------
/** Pull theme + defaults from any of the API response shapes we may get. */
function extractThemePayload(responseData: any): {
  theme: WebsiteThemeSettings | null;
  carousel_images: any[];
  template: string;
  template_label: string;
  defaults: ThemeColorDefaults;
} {
  const root = responseData?.data ?? responseData ?? {};
  // Nested: { theme, defaults } OR flat theme model on data OR website_theme_settings
  const themeCandidate =
    root.theme ??
    root.website_theme_settings ??
    (root.primary_color || root.secondary_color ? root : null);

  const defaultsSource =
    root.defaults ?? root.theme_defaults ?? null;

  const defaults: ThemeColorDefaults = {
    primary_color: defaultsSource?.primary_color || "#00c1cf",
    secondary_color: defaultsSource?.secondary_color || "#373737",
    heading_color: defaultsSource?.heading_color || "#ffffff",
    text_color: defaultsSource?.text_color || "#e9e9e9",
  };

  return {
    theme: themeCandidate || null,
    carousel_images: root.carousel_images || root.images || [],
    template: root.template || root.theme_template || "index",
    template_label: root.template_label || root.theme_template_label || "Template 1",
    defaults,
  };
}

export const getThemeSettings = async (): Promise<{
  theme: WebsiteThemeSettings;
  carousel_images: any[];
  template: string;
  template_label: string;
  defaults: ThemeColorDefaults;
}> => {
  try {
    const response = await api.get("/admin/website-theme-settings");
    const extracted = extractThemePayload(response.data);
    if (!extracted.theme) {
      throw new Error("Theme settings missing in API response");
    }
    return {
      theme: extracted.theme,
      carousel_images: extracted.carousel_images,
      template: extracted.template,
      template_label: extracted.template_label,
      defaults: extracted.defaults,
    };
  } catch (err) {
    console.error("Error fetching theme settings:", err);
    throw err;
  }
};

// -----------------------------Update Theme Settings------------------------------------
export const updateThemeSettings = async (
  id: number,
  data: {
    title: string;
    is_logo_visible: boolean;
    booking_url?: string;
    booking_button_label?: string;
    primary_color?: string;
    secondary_color?: string;
    heading_color?: string;
    text_color?: string;
    logo?: File | null;
    favicon?: File | null;
  }
) => {
  try {
    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("front_title", data.title);
    formData.append("website_title", data.title);
    // Send 1 or 0 as string (backend should check value, not just existence)
    formData.append("is_logo_visible", data.is_logo_visible ? "1" : "0");
    formData.append("booking_url", data.booking_url ?? "");
    formData.append("booking_button_label", data.booking_button_label ?? "");
    if (data.primary_color != null) formData.append("primary_color", data.primary_color);
    if (data.secondary_color != null) formData.append("secondary_color", data.secondary_color);
    if (data.heading_color != null) formData.append("heading_color", data.heading_color);
    if (data.text_color != null) formData.append("text_color", data.text_color);

    if (data.logo instanceof File) {
      let uploadBlob: Blob = data.logo;
      if (data.logo.size > 1 * 1024 * 1024) {
        uploadBlob = await resizeImage(data.logo, 800, 0.8);
      }
      formData.append("front_logo", new File([uploadBlob], data.logo.name, { type: uploadBlob.type }));
    }

    if (data.favicon instanceof File) {
      // Favicons usually need to be small and square (e.g., 32x32)
      const faviconBlob = await resizeImage(data.favicon, 32, 0.9);
      formData.append("favicon", new File([faviconBlob], data.favicon.name, { type: "image/png" }));
    }

    try {
      const response = await api.post(`/admin/website-theme-settings/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Accept": "application/json"
        },
      });
      return response.data;
    } catch (reqErr: any) {
      if (reqErr.response?.status === 404) {
        console.warn("Theme settings update failed (404). Trying generic path...");
        const retryResponse = await api.post("/admin/website-settings/theme", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "Accept": "application/json"
          },
        });
        return retryResponse.data;
      }
      throw reqErr;
    }

  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    throw err;
  }
};

// -----------------------------Reset Theme Colors------------------------------------
export const resetThemeSettings = async (): Promise<{
  theme: WebsiteThemeSettings;
  template: string;
  template_label: string;
  defaults: ThemeColorDefaults;
}> => {
  try {
    const response = await api.post("/admin/website-theme-settings/reset");
    const payload = response.data?.data || response.data;
    return {
      theme: payload.theme,
      template: payload.template || "index",
      template_label: payload.template_label || "Template 1",
      defaults: payload.defaults,
    };
  } catch (err) {
    console.error("Error resetting theme settings:", err);
    throw err;
  }
};

// -----------------------------Update Hero Section------------------------------------
export const updateHeroSection = async (data: {
  title: string;
  tagline: string;
  cta_text: string;
  theme_color: string;
  cover_image?: File | null;
  logo?: File | null;
}) => {
  try {
    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("title", data.title);
    formData.append("tagline", data.tagline);
    formData.append("cta_text", data.cta_text);
    formData.append("theme_color", data.theme_color);

    if (data.cover_image instanceof File) {
      let uploadBlob: Blob = data.cover_image;
      if (data.cover_image.size > 1.5 * 1024 * 1024) {
        uploadBlob = await resizeImage(data.cover_image, 1920, 0.8);
      }
      formData.append("cover_image", new File([uploadBlob], data.cover_image.name, { type: uploadBlob.type }));
    }

    if (data.logo instanceof File) {
      formData.append("logo", data.logo);
    }

    try {
      const response = await api.post("/admin/website-settings/hero-section", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Accept": "application/json"
        },
      });
      return response.data;
    } catch (reqErr: any) {
      if (reqErr.response?.status === 404) {
        console.warn("Hero section update failed (404). Trying to create new settings...");
        formData.delete("_method");
        const retryResponse = await api.post("/admin/website-settings/hero-section", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "Accept": "application/json"
          },
        });
        return retryResponse.data;
      }
      throw reqErr;
    }
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    throw err;
  }
};

export const updateCompanyTemplate = async (templateId: string) => {
  try {
    const response = await api.post("/admin/settings/update-company-template", {
      template: templateId,
    });
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    throw err;
  }
};

// -----------------------------Testimonials Section------------------------------------
export const getTestimonials = async () => {
  try {
    const response = await api.get("/admin/website-testimonials");
    console.log("Testimonials fetched successfully:", response.data);
    return response.data;
  } catch (err: any) {
    console.error("Error fetching testimonials:", err);
    throw err;
  }
};

export const getSingleTestimonial = async (id: number) => {
  try {
    const response = await api.get(`/admin/website-testimonials/${id}`);
    console.log(`Testimonial ${id} fetched successfully:`, response.data);
    return response.data;
  } catch (err: any) {
    console.error(`Error fetching testimonial ${id}:`, err);
    throw err;
  }
};

export const createTestimonial = async (data: {
  client: string;
  feedback: string;
}) => {
  try {
    const response = await api.post("/admin/website-testimonials", {
      ...data,
      rating: 5,
      status: "active",
      image: null,
    });
    console.log("Testimonial created successfully:", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    throw err;
  }
};

export const updateTestimonial = async (
  id: number,
  data: {
    client: string;
    feedback: string;
  }
) => {
  try {
    const response = await api.put(`/admin/website-testimonials/${id}`, {
      ...data,
      rating: 5,
      status: "active",
      image: null,
    });
    console.log(`Testimonial ${id} updated successfully:`, response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    throw err;
  }
};

export const deleteTestimonial = async (id: number) => {
  try {
    const response = await api.delete(`/admin/website-testimonials/${id}`);
    console.log(`Testimonial ${id} deleted successfully:`, response.data);
    return response.data;
  } catch (err: any) {
    console.error(`Error deleting testimonial ${id}:`, err);
    throw err;
  }
};

// -----------------------------Hero Sliders------------------------------------
export const getHeroSliders = async () => {
  try {
    const response = await api.get("/admin/website-sliders");
    console.log("Hero sliders fetched successfully:", response.data);
    return response.data;
  } catch (err: any) {
    console.error("Error fetching hero sliders:", err);
    throw err;
  }
};

export const createHeroSlider = async (data: any) => {
  try {
    const response = await api.post("/admin/website-sliders", data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    throw err;
  }
};

export const updateHeroSlider = async (id: number, data: any) => {
  try {
    let payload = data;

    // Check if data is FormData
    if (data instanceof FormData) {
      data.append("_method", "PUT");
      payload = data;
    } else {
      payload = { ...data, _method: "PUT" };
    }

    const response = await api.post(`/admin/website-sliders/${id}`, payload);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }
    throw err;
  }
};

export const deleteHeroSlider = async (id: number) => {
  try {
    const response = await api.delete(`/admin/website-sliders/${id}`);
    return response.data;
  } catch (err: any) {
    console.error(`Error deleting hero slider ${id}:`, err);
    throw err;
  }
};
